package com.schoolconnect.app.feature.attendance

import com.schoolconnect.app.designsystem.appDateInputToApi
import com.schoolconnect.app.repository.attendance.AttendanceHistoryRepository
import com.schoolconnect.app.util.scLog
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.Job
import kotlinx.coroutines.SupervisorJob
import kotlinx.coroutines.cancel
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.launch

class AttendanceHistoryViewModel(private val studentId: String) {
    private companion object { const val TAG = "[DATA_RETRIEVE_SC] AttendanceHistoryViewModel" }
    private val scope = CoroutineScope(SupervisorJob() + Dispatchers.Default)
    private var loadJob: Job? = null
    private val _uiState = MutableStateFlow(AttendanceHistoryUiState())
    val uiState: StateFlow<AttendanceHistoryUiState> = _uiState.asStateFlow()

    init { load() }

    fun onEvent(event: AttendanceHistoryUiEvent) {
        when (event) {
            is AttendanceHistoryUiEvent.FromDateChanged -> _uiState.update { it.copy(fromDate = event.value) }
            is AttendanceHistoryUiEvent.ToDateChanged -> _uiState.update { it.copy(toDate = event.value) }
            is AttendanceHistoryUiEvent.StatusChanged -> _uiState.update { it.copy(selectedStatus = event.value, pageIndex = 0) }
            AttendanceHistoryUiEvent.ApplyClicked -> {
                _uiState.update { it.copy(pageIndex = 0) }
                load()
            }
            AttendanceHistoryUiEvent.ClearClicked -> {
                _uiState.update { it.copy(fromDate = "", toDate = "", selectedStatus = "", pageIndex = 0) }
                load()
            }
            is AttendanceHistoryUiEvent.PageChanged -> {
                _uiState.update { it.copy(pageIndex = event.pageIndex.coerceAtLeast(0)) }
                load()
            }
            is AttendanceHistoryUiEvent.PageSizeChanged -> {
                _uiState.update { it.copy(pageSize = event.pageSize.coerceIn(10, 100), pageIndex = 0) }
                load()
            }
        }
    }

    private fun load() {
        loadJob?.cancel()
        loadJob = scope.launch {
            val state = _uiState.value
            val fromDate = appDateInputToApi(state.fromDate)
            val toDate = appDateInputToApi(state.toDate)
            if (state.fromDate.isNotBlank() && fromDate == null || state.toDate.isNotBlank() && toDate == null) {
                _uiState.update { it.copy(isLoading = false, errorMessage = "Enter valid dates in DD MMM YYYY format.") }
                return@launch
            }
            _uiState.update { it.copy(isLoading = true, errorMessage = "") }
            AttendanceHistoryRepository.load(
                studentId = studentId,
                fromDate = fromDate,
                toDate = toDate,
                status = state.selectedStatus.toApiStatus().takeIf { it.isNotBlank() },
                pageIndex = state.pageIndex,
                pageSize = state.pageSize
            ).fold(
                onSuccess = { response ->
                    val analytics = response.analytics
                    _uiState.update {
                        it.copy(
                            isLoading = false,
                            errorMessage = "",
                            totalDays = analytics?.totalDays ?: 0,
                            attendancePercentage = (analytics?.attendancePercentage ?: 0.0).toInt(),
                            presentDays = analytics?.presentDays ?: 0,
                            absentDays = analytics?.absentDays ?: 0,
                            ledger = response.ledger.map { row ->
                                AttendanceHistoryLedgerUi(
                                    date = formatHistoryDate(row.attendanceDate),
                                    day = dayOfWeekForIsoDate(row.attendanceDate),
                                    status = row.status.toUiStatus(),
                                    remarks = row.remarks.orEmpty()
                                )
                            },
                            totalCount = response.total ?: response.ledger.size
                        )
                    }
                },
                onFailure = { throwable ->
                    scLog("$TAG load failure studentId=$studentId", throwable)
                    _uiState.update { it.copy(isLoading = false, errorMessage = throwable.message.orEmpty().ifBlank { "Unable to load attendance history." }) }
                }
            )
        }
    }

    fun clear() { loadJob?.cancel(); scope.cancel() }
}

private fun String?.toApiStatus(): String =
    this.orEmpty().trim().takeIf { it.isNotBlank() && !it.equals("All", ignoreCase = true) }?.let {
        com.schoolconnect.app.repository.systemlookup.SystemLookupOptions.serverCode("ATTENDANCE_STATUS", it)
    }.orEmpty()

private fun String?.toUiStatus(): String = when (this.orEmpty().trim().lowercase()) {
    "present", "p" -> "P"
    "absent", "a" -> "A"
    "absent morning", "a-m" -> "A-M"
    "absent afternoon", "a-a" -> "A-A"
    else -> this.orEmpty().ifBlank { "—" }
}

private fun formatHistoryDate(value: String?): String {
    val parts = value.orEmpty().take(10).split("-")
    if (parts.size != 3) return value.orEmpty().ifBlank { "—" }
    val month = listOf("Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec")
        .getOrNull(parts[1].toIntOrNull()?.minus(1) ?: -1) ?: return value.orEmpty()
    return "${parts[2].padStart(2, '0')} $month ${parts[0]}"
}

private fun dayOfWeekForIsoDate(value: String?): String {
    val parts = value.orEmpty().take(10).split("-").mapNotNull(String::toIntOrNull)
    if (parts.size != 3) return "—"
    var year = parts[0]
    val month = parts[1]
    val day = parts[2]
    val offsets = intArrayOf(0, 3, 2, 5, 0, 3, 5, 1, 4, 6, 2, 4)
    if (month < 3) year -= 1
    return listOf("Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat")[(year + year / 4 - year / 100 + year / 400 + offsets[month - 1] + day) % 7]
}
